import { createContext } from '@radix-ui/react-context';
import { PropsWithChildren, useCallback } from 'react';

export type TimeZoneContext = {
  /** IANA timezone string, e.g. "America/New_York" */
  timeZone: string;
  /** Returns short UTC offset string like "UTC-5" */
  getUTCOffset: () => string | undefined;
  /** Describes the source of this timezone, e.g. "Site timezone" */
  label?: string;
  /** Name of the entity this timezone belongs to, e.g. "Auckland Warehouse" */
  entityName?: string;
};

const getBrowserTimeZone = () => Intl.DateTimeFormat().resolvedOptions().timeZone;

const getUTCOffsetForTimeZone = (timeZone: string) => {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone,
    timeZoneName: 'shortOffset',
  });
  return formatter.formatToParts(new Date()).find((part) => part.type === 'timeZoneName')?.value;
};

const [Provider, useContext] = createContext<TimeZoneContext>(
  'TimezoneProvider',
  {
    timeZone: getBrowserTimeZone(),
    getUTCOffset: () => getUTCOffsetForTimeZone(getBrowserTimeZone()),
    label: undefined,
    entityName: undefined,
  }
);

interface TimezoneProviderProps {
  timeZone?: string;
  /** Describes the source of this timezone, e.g. "Site timezone" */
  label?: string;
  /** Name of the entity this timezone belongs to, e.g. site name */
  entityName?: string;
}

/**
 * Provides a contextual timezone to child components.
 *
 * Useful for injecting context-aware timezones like site timezones.
 * When not present, consumers fall back to the browser's local timezone.
 */
export const TimezoneProvider = ({
  children,
  timeZone,
  label,
  entityName,
}: PropsWithChildren<TimezoneProviderProps>) => {
  const resolvedTimeZone = timeZone || getBrowserTimeZone();

  const getUTCOffset = useCallback(() => {
    return getUTCOffsetForTimeZone(resolvedTimeZone);
  }, [resolvedTimeZone]);

  return (
    <Provider
      timeZone={resolvedTimeZone}
      getUTCOffset={getUTCOffset}
      label={label}
      entityName={entityName}
    >
      {children}
    </Provider>
  );
};

export const useContextualTimeZone = () => useContext('TimeZone');
